// PipelineStageTab — generic tab for each pipeline stage.
// Handles three stage types seamlessly:
//   • Standard AI stages   → invite link to /stage-interview
//   • Questionnaire stages → invite email + /stage-questionnaire, auto-scores on submit
//   • Human interview      → AI/Human track assignment, Google Meet panel link, Hire/Hold/Reject

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Send, RefreshCw, CheckCircle, XCircle, Clock, Mail, Users, Award, Shield,
  ClipboardList, Video, UserCheck, Bot, ExternalLink, ChevronDown, ChevronUp,
  AlertTriangle, ThumbsUp, Eye, CheckSquare, Square, X,
} from 'lucide-react';
import BulkPreviewModal from '../../components/common/modal/BulkPreviewModal';
import api from '../../services/api';
import config from '../../config/config';

// ─── helpers ──────────────────────────────────────────────────────────────────

const STATUS_BADGE = {
  shortlisted: { label: 'Passed',      cls: 'bg-green-100 text-green-700',  Icon: CheckCircle },
  rejected:    { label: 'Failed',      cls: 'bg-red-100 text-red-700',      Icon: XCircle },
  completed:   { label: 'Completed',   cls: 'bg-blue-100 text-blue-700',    Icon: CheckCircle },
  in_progress: { label: 'In Progress', cls: 'bg-yellow-100 text-yellow-700',Icon: Clock },
  not_started: { label: 'Not Started', cls: 'bg-gray-100 text-gray-500',    Icon: Clock },
  null:        { label: 'Not Invited', cls: 'bg-gray-100 text-gray-400',    Icon: Clock },
};

const INVITE_BADGE = {
  sent:      { label: 'Invited',   cls: 'bg-blue-50 text-blue-600' },
  completed: { label: 'Submitted', cls: 'bg-green-50 text-green-600' },
  pending:   { label: 'Pending',   cls: 'bg-gray-50 text-gray-500' },
};

const HR_DECISION_BADGE = {
  hire:   { label: 'Hire',   cls: 'bg-green-100 text-green-700' },
  hold:   { label: 'Hold',   cls: 'bg-amber-100 text-amber-700' },
  reject: { label: 'Reject', cls: 'bg-red-100 text-red-700' },
};

const TRACK_BADGE = {
  ai:    { label: 'AI Interview',    cls: 'bg-indigo-100 text-indigo-700', Icon: Bot },
  human: { label: 'Human Interview', cls: 'bg-purple-100 text-purple-700', Icon: UserCheck },
};

function ScorePill({ score, passingScore }) {
  if (score == null) return <span className="text-gray-400 text-sm">—</span>;
  const pass = score >= passingScore;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-sm font-semibold
      ${pass ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
      <Award className="w-3 h-3" />{score}%
      <span className="text-xs font-normal opacity-70">/ {passingScore}%</span>
    </span>
  );
}

function ProctoringReport({ examDetails }) {
  const [open, setOpen] = React.useState(false);
  if (!examDetails) return null;
  const { proctoringScore, integrityScore, riskLevel, integrityStatus, violationCount, violations = [] } = examDetails;
  const riskCls = { low: 'text-green-600 bg-green-50', medium: 'text-amber-600 bg-amber-50', high: 'text-red-600 bg-red-50' }[riskLevel] || 'text-gray-500 bg-gray-50';
  return (
    <div className="mt-1">
      <button onClick={() => setOpen(o => !o)} className="text-[11px] text-orange-500 hover:underline flex items-center gap-1">
        <Shield className="w-3 h-3" /> Proctoring {open ? '▲' : '▼'}
      </button>
      {open && (
        <div className="mt-1.5 p-3 bg-gray-50 border rounded-lg text-xs space-y-1">
          <div className="flex flex-wrap gap-2">
            <span className="px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 font-semibold">Score: {proctoringScore ?? '—'}/100</span>
            <span className={`px-2 py-0.5 rounded-full font-semibold ${riskCls}`}>Risk: {riskLevel ?? '—'}</span>
          </div>
          <p className="text-gray-500">{violationCount ?? 0} violation(s)</p>
        </div>
      )}
    </div>
  );
}

function StageSelectionDropdown({ resumeId, jobId, stageId, current, onChanged }) {
  const [saving, setSaving] = useState(false);
  const [value, setValue]   = useState(current || 'not_started');
  React.useEffect(() => { setValue(current || 'not_started'); }, [current]);
  const OPTIONS = [
    { value: 'shortlisted', label: '✓ Shortlisted', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
    { value: 'rejected',    label: '✗ Rejected',    cls: 'bg-red-50 text-red-600 border-red-200' },
    { value: 'completed',   label: '● Completed',   cls: 'bg-blue-50 text-blue-600 border-blue-200' },
    { value: 'not_started', label: '○ Pending',     cls: 'bg-gray-50 text-gray-500 border-gray-200' },
  ];
  const handleChange = async (newVal) => {
    if (newVal === value) return;
    setSaving(true);
    try {
      await api.patch(`/jobs/${jobId}/pipeline-stage/${stageId}/candidates/${resumeId}/status`, { status: newVal });
      setValue(newVal);
      onChanged?.();
      toast.success('Status updated');
    } catch { toast.error('Failed to update status'); }
    finally { setSaving(false); }
  };
  const cur = OPTIONS.find(o => o.value === value) || OPTIONS[3];
  return (
    <select value={value} onChange={e => handleChange(e.target.value)} disabled={saving}
      className={`text-xs font-semibold border rounded-lg px-2.5 py-1.5 cursor-pointer focus:outline-none disabled:opacity-60 ${cur.cls}`}>
      {OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}

// ─── QuestionnaireAnswers inline accordion ────────────────────────────────────
function QuestionnaireAnswers({ answers }) {
  const [open, setOpen] = useState(false);
  if (!answers?.length) return null;
  return (
    <div className="mt-1">
      <button onClick={() => setOpen(o => !o)}
        className="text-[11px] text-indigo-500 hover:underline flex items-center gap-1">
        <ClipboardList className="w-3 h-3" /> {open ? 'Hide answers' : 'View answers'}
        {open ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
      </button>
      {open && (
        <div className="mt-2 p-3 bg-indigo-50 border border-indigo-100 rounded-xl text-xs space-y-3 max-h-56 overflow-y-auto">
          {answers.map((a, i) => (
            <div key={i} className="border-b border-indigo-100 last:border-0 pb-2 last:pb-0">
              <p className="font-medium text-gray-700 mb-1">Q{i + 1}. {a.question}</p>
              {a.questionType === 'mcq' ? (
                <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${a.score > 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                  Option {a.selectedOption != null ? a.selectedOption + 1 : '—'} — {a.score > 0 ? 'Correct' : 'Incorrect'}
                </span>
              ) : (
                <p className="text-gray-600 bg-white rounded p-1.5 leading-relaxed">
                  {a.writtenAnswer || <em className="text-gray-400">No answer</em>}
                </p>
              )}
              <p className="text-gray-400 mt-0.5">{a.score}/{a.maxScore} pts</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Human interview track assignment ────────────────────────────────────────
function TrackAssignment({ candidate, jobId, stageId, onChanged }) {
  const [assigning, setAssigning] = useState(false);
  const sr    = candidate.stageResult;
  const track = sr?.interviewTrack;

  const assign = async (newTrack) => {
    setAssigning(true);
    try {
      await api.post(`/human-interview/${jobId}/${stageId}/assign/${candidate._id}`, { track: newTrack });
      toast.success(newTrack === 'human'
        ? 'Assigned to Human Interview — candidate notified by email'
        : 'Assigned to AI Interview track');
      onChanged?.();
    } catch { toast.error('Failed to assign track'); }
    finally { setAssigning(false); }
  };

  if (track) {
    const tb = TRACK_BADGE[track];
    const Icon = tb.Icon;
    return (
      <div className="flex flex-col gap-1">
        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${tb.cls}`}>
          <Icon className="w-3 h-3" /> {tb.label}
        </span>
        <button onClick={() => assign(track === 'ai' ? 'human' : 'ai')} disabled={assigning}
          className="text-[10px] text-gray-400 hover:text-orange-500">
          Switch to {track === 'ai' ? 'Human' : 'AI'}
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1">
      <p className="text-[10px] text-gray-400 mb-1">Assign track:</p>
      <div className="flex gap-1">
        <button onClick={() => assign('ai')} disabled={assigning}
          className="flex items-center gap-1 px-2 py-1 bg-indigo-50 text-indigo-700 border border-indigo-200 rounded-lg text-[11px] font-medium hover:bg-indigo-100 disabled:opacity-50">
          <Bot className="w-3 h-3" /> AI
        </button>
        <button onClick={() => assign('human')} disabled={assigning}
          className="flex items-center gap-1 px-2 py-1 bg-purple-50 text-purple-700 border border-purple-200 rounded-lg text-[11px] font-medium hover:bg-purple-100 disabled:opacity-50">
          <UserCheck className="w-3 h-3" /> Human
        </button>
      </div>
    </div>
  );
}

// ─── main component ───────────────────────────────────────────────────────────
const PipelineStageTab = ({ stage }) => {
  const { id: jobId } = useParams();
  const navigate = useNavigate();

  const isQuestionnaire  = stage.type === 'questionnaire';
  const isHumanInterview = stage.type === 'human_interview';

  const [candidates, setCandidates]       = useState([]);
  const [loading, setLoading]             = useState(true);
  const [showAll, setShowAll]             = useState(false);
  const [inviting, setInviting]           = useState(null);
  const [bulkInviting, setBulkInviting]   = useState(false);
  const [expiryDate, setExpiryDate]       = useState('');
  const [showExpiryFor, setShowExpiryFor] = useState(null);

  // Bulk email preview+send state
  const [selectedIds,  setSelectedIds]  = useState([]);
  const [previewing,   setPreviewing]   = useState(false);
  const [previewData,  setPreviewData]  = useState(null);
  const [sendingBulk,  setSendingBulk]  = useState(false);

  // ── fetch ──────────────────────────────────────────────────────────────────
  const fetchCandidates = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get(`/jobs/${jobId}/pipeline-stage/${stage.stageId}/candidates`);
      const fetched    = res.data?.data?.candidates || [];
      const stageData  = res.data?.data?.stage || {};
      const passingScore = stageData.passingScore ?? 60;

      // Auto-sync stale statuses (exam or questionnaire completed but status not updated)
      const stale = fetched.filter(c => {
        const r = c.stageResult;
        return r && r.inviteStatus === 'completed' && r.score != null &&
          r.status !== 'shortlisted' && r.status !== 'rejected';
      });

      if (stale.length > 0) {
        await Promise.allSettled(stale.map(c => {
          const expected = c.stageResult.score >= passingScore ? 'shortlisted' : 'rejected';
          return api.patch(`/jobs/${jobId}/pipeline-stage/${stage.stageId}/candidates/${c._id}/status`, { status: expected });
        }));
        const res2 = await api.get(`/jobs/${jobId}/pipeline-stage/${stage.stageId}/candidates`);
        setCandidates(res2.data?.data?.candidates || []);
      } else {
        setCandidates(fetched);
      }
    } catch { toast.error('Failed to load candidates'); }
    finally { setLoading(false); }
  }, [jobId, stage.stageId]);

  useEffect(() => { fetchCandidates(); }, [fetchCandidates]);

  // Auto-poll — fast when invites are pending, slow always
  useEffect(() => {
    const hasPending = candidates.some(c => c.stageResult?.inviteStatus === 'sent');
    if (!hasPending) return;
    const t = setInterval(fetchCandidates, 15000);
    return () => clearInterval(t);
  }, [candidates, fetchCandidates]);

  useEffect(() => {
    const t = setInterval(fetchCandidates, 10000);
    return () => clearInterval(t);
  }, [fetchCandidates]);

  // ── send invite (questionnaire or standard) ────────────────────────────────
  const handleInvite = async (candidate) => {
    setInviting(candidate._id);
    try {
      if (isQuestionnaire) {
        await api.post(`/questionnaire/${jobId}/${stage.stageId}/invite/${candidate._id}`, {
          linkExpiry: expiryDate || undefined,
        });
      } else {
        await api.post(`/jobs/${jobId}/pipeline-stage/${stage.stageId}/invite`, {
          resumeId: candidate._id,
          linkExpiry: expiryDate || undefined,
        });
      }
      toast.success(`Invite sent to ${candidate.candidateName || candidate.email}`);
      setShowExpiryFor(null);
      setExpiryDate('');
      await fetchCandidates();
    } catch { toast.error('Failed to send invite'); }
    finally { setInviting(null); }
  };

  // ── bulk invite all eligible uninvited candidates ──────────────────────────
  const handleBulkInvite = async () => {
    const uninvited = displayed.filter(c =>
      c.eligible && (!c.stageResult?.inviteStatus || c.stageResult.inviteStatus === 'pending')
    );
    if (!uninvited.length) { toast.info('All eligible candidates already invited'); return; }
    setBulkInviting(true);
    let sent = 0, failed = 0;
    for (const c of uninvited) {
      try {
        if (isQuestionnaire) {
          await api.post(`/questionnaire/${jobId}/${stage.stageId}/invite/${c._id}`, {});
        } else {
          await api.post(`/jobs/${jobId}/pipeline-stage/${stage.stageId}/invite`, { resumeId: c._id });
        }
        sent++;
      } catch { failed++; }
    }
    toast.success(`Invites sent: ${sent}${failed ? ` (${failed} failed)` : ''}`);
    setBulkInviting(false);
    await fetchCandidates();
  };

  // ── bulk email preview → approve → send ───────────────────────────────────
  const toggleSelect = (id) =>
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const toggleSelectAll = (displayedList) =>
    setSelectedIds(selectedIds.length === displayedList.length ? [] : displayedList.map(c => c._id));

  const handlePreviewEmails = async () => {
    if (!selectedIds.length) { toast.error('Select at least one candidate'); return; }
    setPreviewing(true);
    try {
      const res = await api.post('/invites/bulk/preview', {
        resumeIds: selectedIds,
        stageId:   stage.stageId,
        jobId,
      });
      setPreviewData(res.data.data);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to generate email previews');
    } finally {
      setPreviewing(false);
    }
  };

  const handleApproveAndSend = async (approvedDrafts) => {
    setSendingBulk(true);
    try {
      const res = await api.post('/invites/bulk', {
        resumeIds: approvedDrafts.map(d => d.resumeId),
        stageId:   stage.stageId,
        jobId,
        drafts:    approvedDrafts,
      });
      const { sent, failed, total } = res.data.data;
      toast.success(`Sent ${sent}/${total} invite emails${failed > 0 ? ` (${failed} failed)` : ''}`);
      setPreviewData(null);
      setSelectedIds([]);
      await fetchCandidates();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Bulk send failed');
    } finally {
      setSendingBulk(false);
    }
  };

  // ── link per stage type ────────────────────────────────────────────────────
  const stageLink = (resumeId) => isQuestionnaire
    ? `${config.baseUrl}/stage-questionnaire/${jobId}/${resumeId}/${stage.stageId}`
    : `${config.baseUrl}/stage-interview/${jobId}/${resumeId}/${stage.stageId}`;

  const panelLink = (resumeId) =>
    `/jobs/${jobId}/human-interview/${stage.stageId}/panel/${resumeId}`;

  // ── display subset ─────────────────────────────────────────────────────────
  const displayed      = showAll ? candidates : candidates.filter(c => c.eligible);
  const eligibleCount  = candidates.filter(c => c.eligible).length;
  const passedCount    = candidates.filter(c => c.stageResult?.status === 'shortlisted').length;
  const decidedCount   = candidates.filter(c => c.stageResult?.hrDecision).length;

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" />
    </div>
  );

  return (
    <div className="space-y-5">
      {/* ── Header ── */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold text-gray-800">{stage.label}</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            {isQuestionnaire && <span className="text-indigo-500 font-medium mr-2">Questionnaire</span>}
            {isHumanInterview && <span className="text-purple-500 font-medium mr-2">Human Interview</span>}
            {!isHumanInterview && <>Pass score: <span className="font-semibold text-orange-500">{stage.passingScore}%</span></>}
            {stage.brief && <span className="ml-3 italic opacity-70">— {stage.brief}</span>}
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <span className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-full text-sm font-medium">
            <Users className="w-4 h-4" /> {eligibleCount} eligible
          </span>
          {!isHumanInterview && (
            <span className="flex items-center gap-1.5 px-3 py-1.5 bg-green-50 text-green-700 rounded-full text-sm font-medium">
              <CheckCircle className="w-4 h-4" /> {passedCount} passed
            </span>
          )}
          {isHumanInterview && decidedCount > 0 && (
            <span className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-50 text-purple-700 rounded-full text-sm font-medium">
              <UserCheck className="w-4 h-4" /> {decidedCount} decided
            </span>
          )}
          {/* Bulk invite — questionnaire or standard AI stages */}
          {!isHumanInterview && (
            <button onClick={handleBulkInvite} disabled={bulkInviting}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-orange-500 text-white rounded-full text-sm font-medium hover:bg-orange-600 disabled:opacity-50">
              {bulkInviting
                ? <RefreshCw className="w-4 h-4 animate-spin" />
                : <Mail className="w-4 h-4" />}
              Invite All Eligible
            </button>
          )}
          {/* Preview & Send — AI-drafted personalised emails */}
          {!isHumanInterview && (
            <button onClick={handlePreviewEmails} disabled={previewing || !selectedIds.length}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-meraki-indigo text-white rounded-full text-sm font-medium hover:bg-indigo-800 disabled:opacity-50">
              {previewing
                ? <RefreshCw className="w-4 h-4 animate-spin" />
                : <Eye className="w-4 h-4" />}
              {previewing ? 'Drafting…' : `Preview & Send${selectedIds.length ? ` (${selectedIds.length})` : ''}`}
            </button>
          )}
          <button onClick={fetchCandidates} className="p-2 hover:bg-gray-100 rounded-full" title="Refresh">
            <RefreshCw className="w-4 h-4 text-gray-400" />
          </button>
        </div>
      </div>

      {/* ── Select-all row ── */}
      {!isHumanInterview && (
        <div className="flex items-center gap-3">
          <button onClick={() => toggleSelectAll(displayed)}
            className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-meraki-indigo transition-colors">
            {selectedIds.length > 0 && selectedIds.length === displayed.length
              ? <CheckSquare className="w-4 h-4 text-meraki-indigo" />
              : <Square className="w-4 h-4" />}
            {selectedIds.length > 0 ? `${selectedIds.length} selected` : 'Select all'}
          </button>
          {selectedIds.length > 0 && (
            <button onClick={() => setSelectedIds([])} className="text-xs text-slate-400 hover:text-slate-600">
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      )}

      {/* ── Filter toggle ── */}
      <div className="flex items-center gap-2">
        <button onClick={() => setShowAll(false)}
          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
            !showAll ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
          Eligible ({eligibleCount})
        </button>
        <button onClick={() => setShowAll(true)}
          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
            showAll ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
          All ({candidates.length})
        </button>
      </div>

      {/* ── Table ── */}
      {displayed.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <Users className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p className="font-medium">
            {showAll ? 'No candidates yet' : 'No candidates have passed the previous stage yet'}
          </p>
          {!showAll && (
            <button onClick={() => setShowAll(true)} className="mt-2 text-sm text-orange-500 hover:underline">
              Show all candidates
            </button>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
          <table className="min-w-full divide-y divide-gray-100">
            <thead className="bg-gray-50">
              <tr>
                {!isHumanInterview && <th className="px-3 py-3 w-8" />}
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Candidate</th>
                {!isHumanInterview && (
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Score</th>
                )}
                {isHumanInterview ? (
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Track</th>
                ) : (
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Status</th>
                )}
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Selection</th>
                {!isHumanInterview && (
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Invite</th>
                )}
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {displayed.map((c) => {
                const sr           = c.stageResult;
                const statusKey    = sr?.status || 'null';
                const badge        = STATUS_BADGE[statusKey] || STATUS_BADGE['null'];
                const BadgeIcon    = badge.Icon;
                const inviteBadge  = sr?.inviteStatus ? INVITE_BADGE[sr.inviteStatus] : INVITE_BADGE.pending;
                const isInviting   = inviting === c._id;
                const alreadySent  = sr?.inviteStatus === 'sent' || sr?.inviteStatus === 'completed';
                const hrDecision   = sr?.hrDecision;

                return (
                  <tr key={c._id} className={`hover:bg-gray-50 transition-colors ${!c.eligible ? 'opacity-50' : ''}`}>
                    {/* Checkbox */}
                    {!isHumanInterview && (
                      <td className="px-3 py-3">
                        <button onClick={() => toggleSelect(c._id)} className="text-slate-400 hover:text-meraki-indigo">
                          {selectedIds.includes(c._id)
                            ? <CheckSquare className="w-4 h-4 text-meraki-indigo" />
                            : <Square className="w-4 h-4" />}
                        </button>
                      </td>
                    )}
                    {/* Candidate */}
                    <td className="px-4 py-3">
                      <p className="font-medium text-gray-900 text-sm">{c.candidateName || '—'}</p>
                      <p className="text-xs text-gray-400">{c.email}</p>
                      {!c.eligible && c.questionnaireGate === 'expired' && (
                        <div className="mt-1 flex items-center gap-1.5">
                          <span className="text-[10px] text-orange-500 font-medium flex items-center gap-1">
                            <AlertTriangle className="w-3 h-3" /> Questionnaire deadline expired
                          </span>
                          <button
                            onClick={async () => {
                              try {
                                await api.post(`/questionnaire/${jobId}/manual-approve/${c._id}`);
                                toast.success('Candidate manually approved for pipeline');
                                fetchCandidates();
                              } catch { toast.error('Failed to approve'); }
                            }}
                            className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-semibold hover:bg-blue-200 flex items-center gap-0.5"
                          >
                            <ThumbsUp className="w-2.5 h-2.5" /> Approve
                          </button>
                        </div>
                      )}
                      {!c.eligible && c.questionnaireGate === 'pending' && (
                        <span className="text-[10px] text-blue-400 font-medium flex items-center gap-1 mt-1">
                          <Clock className="w-3 h-3" /> Questionnaire pending
                          {c.questionnaireDeadline && ` — due ${new Date(c.questionnaireDeadline).toLocaleDateString()}`}
                        </span>
                      )}
                      {!c.eligible && c.questionnaireGate === 'not_sent' && (
                        <span className="text-[10px] text-gray-400 font-medium mt-1 block">Did not pass previous stage</span>
                      )}
                      {!c.eligible && !['expired','pending','not_sent'].includes(c.questionnaireGate) && (
                        <span className="text-[10px] text-red-400 font-medium mt-1 block">Did not pass previous stage</span>
                      )}
                    </td>

                    {/* Score (not for human interview) */}
                    {!isHumanInterview && (
                      <td className="px-4 py-3">
                        <ScorePill score={sr?.score} passingScore={stage.passingScore} />
                        {isQuestionnaire && sr?.questionnaireDetails?.answers && (
                          <QuestionnaireAnswers answers={sr.questionnaireDetails.answers} />
                        )}
                        {!isQuestionnaire && <ProctoringReport examDetails={sr?.examDetails} />}
                      </td>
                    )}

                    {/* Track (human interview) OR Status badge */}
                    <td className="px-4 py-3">
                      {isHumanInterview ? (
                        <div className="space-y-1">
                          <TrackAssignment
                            candidate={c}
                            jobId={jobId}
                            stageId={stage.stageId}
                            onChanged={fetchCandidates}
                          />
                          {hrDecision && (
                            <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold ${HR_DECISION_BADGE[hrDecision]?.cls}`}>
                              {HR_DECISION_BADGE[hrDecision]?.label}
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${badge.cls}`}>
                          <BadgeIcon className="w-3 h-3" /> {badge.label}
                        </span>
                      )}
                    </td>

                    {/* Selection dropdown */}
                    <td className="px-4 py-3">
                      <StageSelectionDropdown
                        resumeId={c._id}
                        jobId={jobId}
                        stageId={stage.stageId}
                        current={sr?.status}
                        onChanged={fetchCandidates}
                      />
                    </td>

                    {/* Invite status (not for human interview) */}
                    {!isHumanInterview && (
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${inviteBadge.cls}`}>
                          {inviteBadge.label}
                        </span>
                        {sr?.inviteSentAt && (
                          <p className="text-[10px] text-gray-400 mt-0.5">
                            {new Date(sr.inviteSentAt).toLocaleDateString()}
                          </p>
                        )}
                      </td>
                    )}

                    {/* Actions */}
                    <td className="px-4 py-3">
                      {c.eligible && (
                        <div className="flex flex-col gap-1.5">
                          {/* Human interview: open panel button */}
                          {isHumanInterview && sr?.interviewTrack === 'human' && (
                            <button
                              onClick={() => navigate(panelLink(c._id))}
                              className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-600 text-white rounded-lg text-xs font-medium hover:bg-purple-700"
                            >
                              <ExternalLink className="w-3 h-3" /> Open Panel
                            </button>
                          )}

                          {/* Standard + questionnaire: send invite */}
                          {!isHumanInterview && (
                            <>
                              {showExpiryFor === c._id ? (
                                <div className="flex items-center gap-2">
                                  <input type="datetime-local" value={expiryDate}
                                    onChange={e => setExpiryDate(e.target.value)}
                                    className="text-xs border rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-orange-400" />
                                  <button onClick={() => handleInvite(c)} disabled={isInviting}
                                    className="flex items-center gap-1 px-2 py-1 bg-orange-500 text-white rounded text-xs hover:bg-orange-600 disabled:opacity-50">
                                    {isInviting ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                                    Send
                                  </button>
                                  <button onClick={() => setShowExpiryFor(null)} className="text-xs text-gray-400 hover:text-gray-600">
                                    Cancel
                                  </button>
                                </div>
                              ) : (
                                <button onClick={() => setShowExpiryFor(c._id)}
                                  className="flex items-center gap-1.5 px-3 py-1.5 border border-orange-300 text-orange-600 rounded-lg text-xs font-medium hover:bg-orange-50">
                                  <Mail className="w-3 h-3" />
                                  {alreadySent ? 'Resend' : 'Send Invite'}
                                </button>
                              )}
                              <button onClick={() => { navigator.clipboard.writeText(stageLink(c._id)); toast.success('Link copied!'); }}
                                className="text-[11px] text-gray-400 hover:text-orange-500 text-left">
                                Copy link
                              </button>
                            </>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {previewData && (
        <BulkPreviewModal
          drafts={previewData.drafts}
          stage={previewData.stage}
          job={previewData.job}
          onApprove={handleApproveAndSend}
          onClose={() => setPreviewData(null)}
          sending={sendingBulk}
        />
      )}
    </div>
  );
};

export default PipelineStageTab;
